import React, { useState } from 'react';
import { Box, Button, Dialog, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * OptimizationModal — the provider-facing CTA funnel.
 *
 * Demo model: we do NOT let the practice edit templates/policy themselves.
 * Every "Improve this" lever funnels here — a diagnostic, lever-specific pitch
 * that books a white-glove optimization session. Our team makes the change off
 * platform; an internal admin marks the intervention, which lands as a marker
 * on the provider's trend (closing the report → refine loop they already see).
 *
 * Visual language is lifted from the jotpsych.com/get-started flow: a soft
 * lavender wash, an indigo display headline under a purple eyebrow, airy
 * shadowless white cards, a deep-indigo pill CTA, and a human "meet your
 * specialist" payoff (the get-started flow's "You'll be meeting Nate" screen).
 * The booking CTA points at Calendly in production.
 */

/** Deep indigo from the JotPsych wordmark — the display-heading ink. */
const INK = '#1f0862';
/** Brand gradient (purple → magenta) from the get-started progress bar. */
const BRAND_GRADIENT = 'linear-gradient(90deg, #6f3ff5 0%, #c44cc0 100%)';

export type OptimizationMetricContext = {
  /** Metric display name, e.g. "Current med-list". */
  name: string;
  /** Scope line, e.g. "MM visits · n=44". */
  appliesTo: string;
  /** Headline value, already formatted, e.g. "51%". */
  standing: string;
  /** The problem framing under the value, e.g. "flat for 90 days · below the 85% benchmark". */
  standingCaption: string;
  /** What we'd change — the lever, e.g. "a carry-forward med-list block in your MM template". */
  fix: string;
  /** Short kind label for the fix chip, e.g. "Template change". */
  fixKind?: string;
  /** Proof line, e.g. "Practices that made this change saw +28 pts in 8 weeks". */
  impact?: string;
  /** Non-display fields for analytics (HIPAA-safe ids/stats); never rendered. */
  analytics?: {
    slug?: string;
    valuePct?: number | null;
    targetPct?: number | null;
    gap?: number | null;
    isOpportunity?: boolean;
  };
};

/** The white-glove specialist the provider meets — overridable, demo default. */
export type OptimizationSpecialist = {
  name: string;
  role: string;
  initials: string;
};

const DEFAULT_SPECIALIST: OptimizationSpecialist = {
  name: 'Jackson Bierfeldt',
  role: 'CTO, JotPsych',
  initials: 'JB',
};

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'primary.main',
    }}
  >
    {children}
  </Typography>
);

const CardLabel: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color = 'text.secondary',
}) => (
  <Typography
    sx={{
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '0.09em',
      textTransform: 'uppercase',
      color,
    }}
  >
    {children}
  </Typography>
);

/** Airy white card on the lavender wash — soft shadow, no hard border. */
const SoftCard: React.FC<{ children: React.ReactNode; accent?: boolean }> = ({
  children,
  accent,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: 3,
        p: 2.25,
        boxShadow: accent
          ? `0 1px 2px ${alpha(theme.palette.warning.main, 0.12)}, 0 10px 28px ${alpha(theme.palette.warning.main, 0.12)}`
          : '0 1px 2px rgba(31,8,98,0.05), 0 10px 28px rgba(31,8,98,0.06)',
        ...(accent && {
          borderLeft: `3px solid ${alpha(theme.palette.warning.main, 0.55)}`,
        }),
      }}
    >
      {children}
    </Box>
  );
};

const PillButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
}> = ({ children, onClick }) => (
  <Button
    onClick={onClick}
    fullWidth
    disableElevation
    sx={{
      bgcolor: INK,
      color: '#fff',
      textTransform: 'none',
      fontSize: 15,
      fontWeight: 700,
      borderRadius: 2,
      py: 1.35,
      '&:hover': { bgcolor: '#2c1080' },
    }}
  >
    {children}
  </Button>
);

export const OptimizationModal: React.FC<{
  open: boolean;
  /** `reachedBooking` is whether the user booked before closing (drop-off analytics). */
  onClose: (reachedBooking: boolean) => void;
  metric: OptimizationMetricContext;
  specialist?: OptimizationSpecialist;
  /** Fired when the provider books — opens Calendly / logs analytics. */
  onBook?: () => void;
}> = ({ open, onClose, metric, specialist = DEFAULT_SPECIALIST, onBook }) => {
  const [booked, setBooked] = useState(false);

  const handleClose = () => {
    onClose(booked);
    setTimeout(() => setBooked(false), 220);
  };

  const handleBook = () => {
    setBooked(true);
    onBook?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #f5f1fc 0%, #ffffff 46%)',
          },
        },
      }}
    >
      {/* Brand gradient top strip (the get-started progress bar) */}
      <Box sx={{ height: 4, background: BRAND_GRADIENT }} />

      {/* Close affordance */}
      <Box
        component="button"
        aria-label="Close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 14,
          right: 16,
          zIndex: 1,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 22,
          lineHeight: 1,
          color: alpha(INK, 0.4),
          '&:hover': { color: INK },
        }}
      >
        ×
      </Box>

      {booked ? (
        /* ---- The human payoff — "meet your specialist" + confirmation ---- */
        <Box sx={{ px: { xs: 3.5, sm: 5 }, pt: 5, pb: 4.5, textAlign: 'center' }}>
          {/* Gradient-ringed avatar */}
          <Box
            sx={{
              width: 92,
              height: 92,
              mx: 'auto',
              mb: 1.75,
              borderRadius: '50%',
              p: '3px',
              background: BRAND_GRADIENT,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                bgcolor: '#efe9fb',
                color: INK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              {specialist.initials}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
            {specialist.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {specialist.role}
          </Typography>

          <Box sx={{ mb: 1.25 }}>
            <Eyebrow>White-glove optimization</Eyebrow>
          </Box>
          <Typography
            sx={{ fontSize: 26, fontWeight: 800, color: INK, lineHeight: 1.15, mb: 1.5 }}
          >
            You&apos;ll be meeting {specialist.name.split(' ')[0]}.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto', mb: 3.5, lineHeight: 1.6 }}
          >
            {specialist.name.split(' ')[0]} and the team will optimize and automate this
            with you. Once it&apos;s live, it lands as a marker on your{' '}
            <Box component="span" sx={{ fontWeight: 700, color: INK }}>
              {metric.name.toLowerCase()}
            </Box>{' '}
            trend — so you can watch it improve.
          </Typography>

          <Box sx={{ maxWidth: 320, mx: 'auto' }}>
            <PillButton onClick={handleClose}>Done</PillButton>
          </Box>
        </Box>
      ) : (
        /* ---- The diagnostic pitch ---- */
        <Box sx={{ px: { xs: 3.5, sm: 5 }, pt: 4, pb: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 2.5 }}>
            <Eyebrow>Practice optimization</Eyebrow>
            <Typography
              sx={{ fontSize: 28, fontWeight: 800, color: INK, lineHeight: 1.12, mt: 0.75 }}
            >
              Let&apos;s optimize your {metric.name.toLowerCase()}.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              JotPsych has a lot of ways to optimize and automate this workflow. Book
              some time and we&apos;ll figure out what moves your{' '}
              {metric.name.toLowerCase()} — then you watch it improve.
            </Typography>
          </Box>

          <Stack spacing={1.75}>
            {/* Diagnostic — where it stands */}
            <SoftCard>
              <CardLabel>Where it stands</CardLabel>
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'baseline', mt: 0.75 }}>
                <Typography sx={{ fontSize: 40, fontWeight: 800, color: INK, lineHeight: 0.9 }}>
                  {metric.standing}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {metric.appliesTo}
                </Typography>
              </Stack>
              <Typography sx={{ color: 'warning.dark', fontWeight: 600, fontSize: 13.5, mt: 1 }}>
                {metric.standingCaption}
              </Typography>
            </SoftCard>

            {/* How we'd help — the optimization offer */}
            <SoftCard accent>
              <Box sx={{ mb: 1 }}>
                <CardLabel color="warning.dark">How we&apos;d help</CardLabel>
              </Box>
              <Typography sx={{ color: INK, fontWeight: 600, fontSize: 15, lineHeight: 1.4 }}>
                We&apos;ll optimize and automate this with you — template changes, smart
                rules, scheduling. A good place to start for {metric.name.toLowerCase()}:{' '}
                {metric.fix}.
              </Typography>
              {metric.impact && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                  {metric.impact}
                </Typography>
              )}
            </SoftCard>
          </Stack>

          {/* How white-glove works — light inline steps */}
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, my: 3 }}
          >
            {['Book a 20-min call', 'We optimize & automate it', 'Watch it improve'].map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && (
                  <Box component="span" sx={{ color: alpha(INK, 0.3), fontSize: 13 }}>
                    →
                  </Box>
                )}
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: alpha(INK, 0.65) }}>
                  {label}
                </Typography>
              </React.Fragment>
            ))}
          </Stack>

          {/* CTA */}
          <PillButton onClick={handleBook}>Pick a time to optimize →</PillButton>
          <Button
            variant="text"
            fullWidth
            onClick={handleClose}
            sx={{ mt: 1, color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}
          >
            Maybe later
          </Button>
        </Box>
      )}
    </Dialog>
  );
};
